
import { computed, defineComponent, onMounted, onUpdated, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useMutation, useQuery } from '@vue/apollo-composable'
import {
  GetEntityByIdDocument,
  TheCarousel,
  CardComponent,
  BaseButton,
  SpeechBubble,
  ImageSource,
  getFileNameByMimeType,
  TestimonyCard,
  BaseIcon,
  BaseInput,
  CreateTestimonyDocument,
  EntityInfo,
  EntityTypes,
  Entity,
  TestimoniCard,
  CircleLoader,
  UpdateEntityDocument,
  GetTestimoniesOfAssetDocument,
} from 'coghent-vue-3-component-library'
import BreadCrumbs, { useHistory } from './BreadCrumbs.vue'
import TheGrid from './TheGrid.vue'
import { useI18n } from 'vue-i18n'
import { useCCModal } from './CreativeModal.vue'
import { useDetailsModal } from './DetailsModal.vue'
import { iiif } from '@/app'
import { ConfigStore, useTestimonyFeature } from '../stores/ConfigStore'
import { UserStore } from '../stores/UserStore'
import StoreFactory from '@/stores/StoreFactory'
import { parseDateAsLocaleString } from '@/helpers'
import ToolTip, { useTooltip } from './ToolTip.vue'
import EntityActions from './EntityActions.vue'
import { useOffensiveContentModal } from './OffensiveContentModal.vue'

type TypeObject = {
  id: string
  label: string
  relation: string
}

const asString = (x: string | string[]) => (Array.isArray(x) ? x[0] : x)

const metaDataInLabel: string[] = [
  'object_category',
  'MensgemaaktObject.draagt',
  'Entiteit.maaktDeelUitVan',
  'MaterieelDing.productie',
  'MensgemaaktObject.maaktDeelUitVan',
  'MaterieelDing.bestaatUit',
  'MaterieelDing.isOvergedragenBijVerwerving',
  'Entiteit.classificatie',
  'Entiteit.wordtNaarVerwezenDoor',
]

export default defineComponent({
  name: 'EntityDetails',
  components: {
    TheGrid,
    CardComponent,
    TheCarousel,
    BaseButton,
    BreadCrumbs,
    BaseInput,
    BaseIcon,
    SpeechBubble,
    CircleLoader,
    ToolTip,
    EntityActions,
  },
  setup: () => {
    const id = ref<string>(asString(useRoute().params['entityID']))
    const router = useRouter()
    const route = useRoute()
    const baseTestimony = ref<typeof EntityInfo>({ title: 'Testimony', description: '', type: EntityTypes.Testimony })
    const { result, loading, refetch } = useQuery(GetEntityByIdDocument, { id: id.value })
    const { onResult: onTestimonyResult, refetch: refetchTestimony } = useQuery(GetTestimoniesOfAssetDocument, { assetId: id.value })
    const { mutate: createTestimony, loading: loadingNewTestimony, onDone: onCreatedTestimony } = useMutation(CreateTestimonyDocument)
    const { mutate: updateEntity } = useMutation(UpdateEntityDocument)
    const carouselFiles = ref<typeof ImageSource[] | undefined>()
    const mediaFiles = ref<any | undefined>()
    const types = ref<any[] | undefined>()
    const relatedItemIds = ref<string[]>([])
    const { openCCModal } = useCCModal()
    const { openDetailsModal, setEntity } = useDetailsModal()
    const { generateUrl, generateInfoUrl, noImageUrl } = iiif
    const { addPageToHistory, history } = useHistory()
    const testimonies = ref<typeof TestimonyCard[]>([])
    const carouselPictureIndex = ref<number>(0)
    const isWritingTestimony = ref<boolean>(false)
    const { updateToolTipShownState, toolTipState } = useTooltip()
    const userStore = StoreFactory.get(UserStore)
    const configStore = StoreFactory.get(ConfigStore)
    const mediafileUrl = configStore.config.value.graphQlLink.replace('graphql', 'mediafile')
    const { openOffensiveContentModal } = useOffensiveContentModal()
    const { t } = useI18n()

    const isDownloadable = computed(() => {
      if (mediaFiles.value && carouselPictureIndex.value !== undefined) {
        return mediaFiles.value[carouselPictureIndex.value].metadata.find((data: any) => data.key == 'publication_status').value !== 'beschermd' ? true : false
      } else {
        return false
      }
    })

    watch(
      () => route.fullPath,
      () => {
        if (route.params.entityID) {
          refetch({ id: asString(route.params.entityID) })
        }
      },
      { immediate: true }
    )

    const parseTestimonyCards = (testimonies: typeof Entity[]): typeof TestimoniCard[] => {
      const testimonyCards: typeof TestimoniCard[] = []
      if (testimonies) {
        testimonies.forEach((testimony: typeof Entity) => {
          const card: typeof TestimoniCard = {
            id: testimony.id,
            name: testimony.user,
            date: parseDateAsLocaleString(new Date(testimony.date[0].value)),
            content: testimony.description[0].value,
            likes: testimony.likes[0].value,
          }
          testimonyCards.push(card)
        })
      }
      return testimonyCards
    }

    const setEntityInformation = (queryResult: any) => {
      if (queryResult.Entity) {
        document.title = `De Collectie van de Gentenaar ${queryResult.Entity.title[0].value ? '| ' + queryResult.Entity.title[0].value : ''}`
        refetchTestimony()
        const photosArray: typeof ImageSource[] = []
        mediaFiles.value = queryResult.Entity?.mediafiles
        queryResult.Entity?.mediafiles.forEach((value: any) => {
          if (value) {
            const filename: string | undefined = getFileNameByMimeType(value)
            if (filename) {
              const isLink: boolean = filename?.includes('https://')
              if (filename) {
                photosArray.push({
                  imageUrl: isLink ? filename : generateUrl(filename, 'full'),
                  infoJson: generateInfoUrl(filename),
                  fallBackUrl: isLink ? filename : generateUrl(filename, 'full', 'max'),
                  noImageUrl: noImageUrl,
                  mediaType: value.mediatype,
                })
              }
            }
          }
        })
        carouselFiles.value = photosArray.length === 0 ? [{ imageUrl: noImageUrl, infoJson: noImageUrl, fallBackUrl: noImageUrl }] : photosArray

        const typeArray: any[] = []
        // queryResult.data.Entity?.metadata.forEach((value: any) => {
        //   if (metaDataInTag.includes(value.key)) {
        //     typeArray.push(value.value)
        //   }
        // })
        queryResult.Entity?.relations.forEach((value: any) => {
          if (!metaDataInLabel.includes(value.label)) {
            value.value && typeArray.push({ label: value.value, id: value.key, relation: value.label })
          }
        })
        types.value = typeArray
        const typeArrayForRelatedAssets = typeArray.filter((type: any) => type.relation != 'MaterieelDing.beheerder')
        relatedItemIds.value = filterDuplicateTypes(typeArrayForRelatedAssets).map((type: any) => type.id)

        if (result.value && result.value.Entity) {
          setEntity({ ...result.value.Entity, types: typeArray })
          addPageToHistory(result.value.Entity.title[0]?.value ? result.value.Entity.title[0].value : 'unknown entity', router.currentRoute.value.fullPath)
        }
      } else {
        router.push({ path: '/entity/not-found', query: route.query })
      }
    }

    watch(
      () => result.value,
      (queryResult: any) => {
        setEntityInformation(queryResult)
      }
    )

    onTestimonyResult((res) => {
      testimonies.value = parseTestimonyCards(res.data.GetTestimoniesOfAsset)
    })

    onCreatedTestimony((res) => {
      testimonies.value = parseTestimonyCards(res.data.CreateTestimony)
    })

    const filterDuplicateTypes = (_relations: Array<TypeObject>) => {
      let myRelations: Array<TypeObject> = []
      Object.assign(myRelations, _relations)
      for (const relation of myRelations) {
        const items = myRelations.filter((_relation) => _relation.id == relation.id)
        if (items.length > 1) {
          const duplicateWithSameValue = items.filter((_item) => _item.label == relation.label)
          if (duplicateWithSameValue.length > 1) {
            myRelations.splice(myRelations.indexOf(relation), 1)
          }
        }
      }
      return myRelations
    }

    const goToRelation = (metaData: any) => {
      let routerLink: string = ''
      if (metaData.id && metaData.relation == 'vervaardiger') {
        routerLink = '/creator/' + metaData.id.replace('entities/', '')
      } else {
        history.value[history.value.length - 1]
        routerLink = '/relation/' + metaData.id.replace('entities/', '')
      }
      if (routerLink) {
        router.push({ path: routerLink, query: route.query })
      }
    }

    const setPictureIndex = (newIndex: number) => {
      carouselPictureIndex.value = newIndex
    }

    const updateTestimonyLikes = (testimony: typeof TestimonyCard) => {
      if (userStore.hasUser) {
        const testimonyToUpdate = testimonies.value.find((element: typeof TestimonyCard) => element.id == testimony.id)
        testimonyToUpdate.likes++
        const testimonyEntity = JSON.parse(JSON.stringify(testimonies.value.find((element: typeof Entity) => element.id == testimony.id)))
        const newMetadata = [{ key: 'likes', value: testimonyToUpdate.likes.toString() }]
        updateEntity({ id: testimony.id, metadata: newMetadata, relations: [] })
      }
    }

    const writeTestimony = () => {
      if (userStore.hasUser) {
        isWritingTestimony.value = !isWritingTestimony.value
      } else {
        updateToolTipShownState(true)
      }
    }

    const createNewTestimony = (body: string) => {
      if (body.length >= 4) {
        baseTestimony.value.description = body
        isWritingTestimony.value = false
        createTestimony({ entityInfo: baseTestimony.value, assetId: result.value.Entity.id })
      }
    }

    return {
      result,
      t,
      carouselFiles,
      types,
      openCCModal,
      openDetailsModal,
      mediaFiles,
      router,
      loading,
      route,
      filterDuplicateTypes,
      goToRelation,
      relatedItemIds,
      testimonies,
      updateTestimonyLikes,
      generateUrl,
      setPictureIndex,
      carouselPictureIndex,
      writeTestimony,
      isWritingTestimony,
      createNewTestimony,
      useTestimonyFeature,
      UserStore,
      loadingNewTestimony,
      mediafileUrl,
      toolTipState,
      isDownloadable,
      openOffensiveContentModal,
    }
  },
})
